class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.essentialUpsellObserver = null;
  }

  startEssentialUpsellObserver() {
    if (this.essentialUpsellObserver) return;
    this.essentialUpsellObserver = new MutationObserver(() => {
      this.relocateEssentialUpsell({ attempts: 0 });
    });
    this.essentialUpsellObserver.observe(document.body, { childList: true, subtree: true });

    window.setTimeout(() => this.stopEssentialUpsellObserver(), 10000);
  }

  stopEssentialUpsellObserver() {
    if (!this.essentialUpsellObserver) return;
    this.essentialUpsellObserver.disconnect();
    this.essentialUpsellObserver = null;
  }

  setEssentialUpsellSingleMode({ mount, widgetRoot, attempts = 12, delayMs = 200 } = {}) {
    if (!mount || !widgetRoot) return;

    const productsRoot = widgetRoot.querySelector('[data-essential-upsell-element="products"]') || widgetRoot;

    // Collect unique variant IDs; this stays 1 even if the app duplicates DOM nodes.
    const variantIdEls = Array.from(productsRoot.querySelectorAll('[data-variant-id]'));
    const variantIds = new Set(
      variantIdEls
        .map((el) => el && el.getAttribute && el.getAttribute('data-variant-id'))
        .filter((v) => v && String(v).trim().length)
        .map((v) => String(v).trim())
    );

    // If the widget hasn't finished rendering yet, retry briefly.
    if (variantIds.size === 0) {
      mount.classList.remove('cart-drawer__essential-upsell--single');
      if (attempts > 0) {
        window.setTimeout(
          () => this.setEssentialUpsellSingleMode({ mount, widgetRoot, attempts: attempts - 1, delayMs }),
          delayMs
        );
      }
      return;
    }

    mount.classList.toggle('cart-drawer__essential-upsell--single', variantIds.size === 1);
  }

  relocateEssentialUpsell({ attempts = 40, delayMs = 250 } = {}) {
    const drawerRoot = document.getElementById('CartDrawer');
    if (!drawerRoot) return;

    const mount = drawerRoot.querySelector('[data-essential-upsell-mount]');
    if (!mount) return;

    // Already mounted
    if (mount.querySelector('.cart-drawer__essential-upsell-root')) return;

    const selectors = [
      '[data-essential-upsell-element="container"]',
      '[data-essential-upsell]',
      '[data-app*="essential-upsell" i]',
      '[id*="essential" i][id*="upsell" i]',
      '[class*="essential" i][class*="upsell" i]',
      'iframe[src*="essential-upsell" i]',
      'iframe[id*="essential" i][id*="upsell" i]',
      'iframe[class*="essential" i][class*="upsell" i]',
    ];

    let candidate = null;
    for (const selector of selectors) {
      const matches = Array.from(document.querySelectorAll(selector));
      candidate = matches.find((el) => {
        if (!(el instanceof HTMLElement)) return false;
        if (el.closest('[data-essential-upsell-mount]')) return false;
        // Avoid grabbing the entire cart drawer or unrelated containers
        if (el.id === 'CartDrawer' || el.classList.contains('drawer') || el.classList.contains('drawer__inner')) return false;
        return true;
      });
      if (candidate) break;
    }

    if (!candidate) {
      if (attempts > 0) {
        window.setTimeout(() => this.relocateEssentialUpsell({ attempts: attempts - 1, delayMs }), delayMs);
      }
      return;
    }

    const appSelectors =
      'ESSENTIAL-UPSELL, [data-essential-upsell-element="container"], [data-essential-upsell], [data-app*="essential-upsell" i], [id*="essential" i][id*="upsell" i], [class*="essential" i][class*="upsell" i]';

    // Start from the closest obvious app container.
    let widgetRoot = candidate.closest(appSelectors) || candidate;

    // Some installations wrap the widget in a fixed/absolute positioned parent that doesn't match our selectors.
    // If the parent is positioned and clearly contains the widget, prefer that so we can neutralize the floating.
    let safety = 0;
    while (widgetRoot.parentElement && safety < 8) {
      safety += 1;
      const parent = widgetRoot.parentElement;
      if (parent === document.body || parent === drawerRoot) break;
      if (!(parent instanceof HTMLElement)) break;

      const parentLooksRelated = parent.matches(appSelectors) || parent.querySelector(appSelectors);
      if (!parentLooksRelated) break;

      const pos = window.getComputedStyle(parent).position;
      const isPositioned = pos === 'fixed' || pos === 'absolute' || pos === 'sticky';
      if (!isPositioned) break;

      widgetRoot = parent;
    }

    widgetRoot.classList.add('cart-drawer__essential-upsell-root');

    // The app often uses fixed/absolute positioning; neutralize it so it can sit above checkout.
    widgetRoot.style.setProperty('position', 'static', 'important');
    widgetRoot.style.setProperty('inset', 'auto', 'important');
    widgetRoot.style.setProperty('top', 'auto', 'important');
    widgetRoot.style.setProperty('right', 'auto', 'important');
    widgetRoot.style.setProperty('bottom', 'auto', 'important');
    widgetRoot.style.setProperty('left', 'auto', 'important');
    widgetRoot.style.setProperty('transform', 'none', 'important');
    widgetRoot.style.setProperty('width', '100%', 'important');
    widgetRoot.style.setProperty('max-width', '100%', 'important');

    mount.appendChild(widgetRoot);

    // Mark single-product offers for mobile styling (no carousel / fit-width).
    this.setEssentialUpsellSingleMode({ mount, widgetRoot });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // Add active class first to make drawer visible
    this.classList.add('active');
    
    // Use requestAnimationFrame for smooth animation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('animate');
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    this.startEssentialUpsellObserver();

    // Essential Upsell loads async; try to mount it after open.
    this.relocateEssentialUpsell();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    this.stopEssentialUpsellObserver();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

      // Allow callers to re-render without forcing the drawer to open.
      // This avoids the drawer reopening after the user closes it while async updates complete.
      const shouldAutoOpen = !(parsedState && parsedState.noOpen);
      if (shouldAutoOpen && !this.classList.contains('active')) {
        this.open();
      }

      // After re-rendering drawer contents, re-mount the upsell widget.
      this.relocateEssentialUpsell();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.pendingUpdate = null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    // Prevent multiple simultaneous requests
    if (this.pendingUpdate) {
      return;
    }
    
    this.pendingUpdate = line;
    this.enableLoading(line);

    // Optimistically update the UI immediately for perceived performance
    const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
    if (quantityElement) {
      quantityElement.value = quantity;
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        
        if (parsedState.errors) {
          // Revert optimistic update on error
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute('value');
          }
          this.updateLiveRegions(line, parsedState.errors);
          this.disableLoading(line);
          this.pendingUpdate = null;
          return;
        }

        // Fast UI update without full section re-render
        this.updateCartDrawerUI(parsedState, line, quantityElement);

        this.updateLiveRegions(line, '');
        
        const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          trapFocus(this.closest('cart-drawer'), lineItem.querySelector(`[name="${name}"]`));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((error) => {
        console.error('Cart update error:', error);
        // Revert optimistic update on error
        if (quantityElement) {
          quantityElement.value = quantityElement.getAttribute('value');
        }
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        this.disableLoading(line);
        this.pendingUpdate = null;
      });
  }

  updateCartDrawerUI(parsedState, line, quantityElement) {
    const lineItem = parsedState.items[line - 1];
    const cartDrawer = this.closest('cart-drawer');
    
    // Update cart item count and empty state
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartDrawer) {
      cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    // If item was removed completely
    if (!lineItem || quantity === 0) {
      const lineItemElement = document.getElementById(`CartDrawer-Item-${line}`);
      if (lineItemElement) {
        lineItemElement.remove();
      }
      
      // If cart is now empty, show empty state
      if (parsedState.item_count === 0) {
        const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
        if (drawerInner && parsedState.sections && parsedState.sections['cart-drawer']) {
          const html = this.getSectionInnerHTML(parsedState.sections['cart-drawer'], '.drawer__inner');
          drawerInner.innerHTML = html;
        }
      }
    } else {
      // Update quantity input
      if (quantityElement) {
        quantityElement.value = lineItem.quantity;
        quantityElement.setAttribute('value', lineItem.quantity);
      }

      // Update line item total price
      const lineItemElement = document.getElementById(`CartDrawer-Item-${line}`);
      if (lineItemElement && lineItem.final_line_price !== undefined) {
        const lineTotalElements = lineItemElement.querySelectorAll('.cart-item__totals .price');
        if (lineTotalElements.length) {
          const formattedLinePrice = this.formatMoney(lineItem.final_line_price);
          lineTotalElements.forEach(el => el.textContent = formattedLinePrice);
        }
      }
    }

    // Update cart subtotal in drawer footer
    const cartSubtotalElements = document.querySelectorAll('.cart-drawer__footer .totals__subtotal-value');
    if (cartSubtotalElements.length && parsedState.total_price !== undefined) {
      const formattedPrice = this.formatMoney(parsedState.total_price);
      cartSubtotalElements.forEach(el => el.textContent = formattedPrice);
    }

    // Update cart icon bubble
    if (parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        const html = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble'], '.shopify-section');
        const shopifySection = cartIconBubble.querySelector('.shopify-section');
        if (shopifySection) {
          shopifySection.innerHTML = html;
        }
      }
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      const format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      return Shopify.formatMoney(cents, format);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
